import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("login"); // "login" | "otp"
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState("");

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await login(email, password);
      if (res?.otp_required) {
        setSentTo(res.sent_to || "");
        setStep("otp");
        setCode("");
        toast.success("We emailed you a verification code");
      } else {
        toast.success("Welcome back!");
        navigate("/admin", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await verifyOtp(email, code);
      toast.success("Welcome back!");
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await resendOtp(email, password);
      setSentTo(res.sent_to || sentTo);
      toast.success("A fresh code is on its way");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-blush px-4" data-testid="admin-login-page">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-25 mix-blend-multiply bg-cover bg-top"
        style={{ backgroundImage: "url(/brand/watercolour.jpg)" }}
      />
      <img src="/brand/floral-tl.png" alt="" aria-hidden className="pointer-events-none absolute left-0 top-0 w-48 opacity-80" />
      <img src="/brand/floral-br.png" alt="" aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-56 opacity-80" />

      {step === "login" ? (
        <form
          onSubmit={submit}
          className="relative z-10 w-full max-w-sm rounded-3xl border border-gold/40 bg-white/85 p-8 shadow-[0_24px_70px_-24px_rgba(123,45,78,0.4)] backdrop-blur"
          data-testid="admin-login-form"
        >
          <div className="mb-6 flex flex-col items-center text-center">
            <img src="/brand/logo.png" alt="Miss Cupcake Creations logo" className="h-20 w-20 rounded-full ring-1 ring-gold/50" />
            <h1 className="mt-4 font-serif text-2xl font-semibold text-burgundy">Owner Login</h1>
            <p className="mt-1 font-script text-lg text-gold">Celebrate it uniquely</p>
          </div>

          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-wine/70" htmlFor="admin-email">
            Email
          </label>
          <input
            id="admin-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input"
            className="mb-4 w-full rounded-xl border border-gold/40 bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/25"
            placeholder="you@example.com"
          />

          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-wine/70" htmlFor="admin-password">
            Password
          </label>
          <input
            id="admin-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="mb-5 w-full rounded-xl border border-gold/40 bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/25"
            placeholder="••••••••"
          />

          {error && (
            <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="admin-login-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full border border-gold bg-gold py-3 text-sm font-semibold uppercase tracking-[0.15em] text-white transition-all hover:-translate-y-0.5 hover:bg-golddeep disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            Sign in
          </button>

          <a href="/admin/forgot-password" className="mt-4 block text-center text-xs font-medium text-burgundy/70 underline decoration-gold/40 underline-offset-2 hover:text-gold" data-testid="login-forgot-link">
            Forgot your password?
          </a>

          <a href="/" className="mt-3 block text-center text-xs text-wine/60 hover:text-gold" data-testid="login-back-to-site">
            ← Back to the website
          </a>
        </form>
      ) : (
        <form
          onSubmit={submitOtp}
          className="relative z-10 w-full max-w-sm rounded-3xl border border-gold/40 bg-white/85 p-8 shadow-[0_24px_70px_-24px_rgba(123,45,78,0.4)] backdrop-blur"
          data-testid="admin-otp-form"
        >
          <div className="mb-6 flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 ring-1 ring-gold/50">
              <ShieldCheck className="h-8 w-8 text-gold" />
            </div>
            <h1 className="mt-4 font-serif text-2xl font-semibold text-burgundy">Verify it&#39;s you</h1>
            <p className="mt-1 text-xs text-wine/70">
              We sent a 6-digit code to{" "}
              <span className="font-semibold text-burgundy" data-testid="otp-sent-to">{sentTo || "your email"}</span>
            </p>
          </div>

          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-wine/70" htmlFor="admin-otp">
            Verification code
          </label>
          <input
            id="admin-otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            data-testid="admin-otp-input"
            className="mb-5 w-full rounded-xl border border-gold/40 bg-white px-4 py-2.5 text-center text-lg tracking-[0.5em] outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/25"
            placeholder="••••••"
          />

          {error && (
            <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="admin-otp-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || code.length < 6}
            data-testid="admin-otp-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full border border-gold bg-gold py-3 text-sm font-semibold uppercase tracking-[0.15em] text-white transition-all hover:-translate-y-0.5 hover:bg-golddeep disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Verify &amp; sign in
          </button>

          <button
            type="button"
            onClick={resend}
            disabled={busy}
            data-testid="admin-otp-resend"
            className="mt-4 block w-full text-center text-xs font-medium text-burgundy/70 underline decoration-gold/40 underline-offset-2 hover:text-gold disabled:opacity-60"
          >
            Didn&#39;t get it? Resend code
          </button>

          <button
            type="button"
            onClick={() => { setStep("login"); setError(""); setCode(""); }}
            data-testid="admin-otp-back"
            className="mt-3 block w-full text-center text-xs text-wine/60 hover:text-gold"
          >
            ← Use a different account
          </button>
        </form>
      )}
    </div>
  );
}
